import { Show } from '@refinedev/antd';
import {
  useShow,
  useCan,
  usePermissions,
  useKeys,
  useResource,
  pickDataProvider,
} from '@refinedev/core';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Typography,
  Descriptions,
  Tabs,
  Button,
  Space,
  Spin,
  Result,
  Alert,
} from 'antd';
import { EditOutlined, ExportOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SsoConfigTab } from './components/sso';
import { getCompanySsoConfig } from './components/sso/api';
import { ssoStateOf, type SsoState } from './components/sso/ssoState';
import { CsatDomainTab } from './components/csat/CsatDomainTab';
import {
  CSAT_DOMAIN_READ_PERMISSION,
  CSAT_DOMAIN_WRITE_PERMISSION,
} from './api/csatDomainClient';
import { hasPermission } from '../../utils/permissions';
import { authClient } from '../../providers';
import { DeleteCompanySection } from './components/DeleteCompanySection';
import { DemoDataSection } from './components/DemoDataSection';
import { FeatureFlagsTab } from './components/FeatureFlagsTab';
import { HelpDeskTab } from './components/HelpDeskTab';
import { HealthTab } from './components/health/HealthTab';
import { SecurityTab } from './components/security/SecurityTab';
import {
  AccountTypeSetting,
  ActiveSetting,
  SalesChannelSetting,
} from './components/CompanySettingsInline';
import { useSaveCompanyField } from './components/useSaveCompanyField';
import { clearCompanyReadFailed } from './components/companyReadFailedStore';
import { sendingDomainKey } from './api/helpDeskSendingDomainClient';
import { ServeFirstLiveTag } from './components/ServeFirstLiveTag';
import { COMPANIES_META } from './constants';
import { createdByLabel } from './companyCreator';
import { useCanEditCompany } from './api/editableCompaniesClient';
import type { CompanyDeletionRow } from './api/companyDeleteClient';
import { usePlatformOnCompanyFields } from '../platformFlags/api/platformFlagsClient';
import { WebAppLink } from '../../components/WebAppLink';
import { ChangeLogLink } from '../../components/changeLog';
import type { Company } from './list';

const SSO_WRITE_PERMISSION = 'admin:sso:write';

/** Tabs a link can open with `?tab=`; the others open only by clicking them. */
const URL_TABS = new Set(['health']);
const tabFromUrl = (params: URLSearchParams): string | null => {
  const tab = params.get('tab');
  return tab && URL_TABS.has(tab) ? tab : null;
};

/** The company's name, but only when the loaded record is the one in the URL. */
const currentCompanyName = (c: Company | undefined, id: string | undefined) =>
  c && id && String(c._id) === id ? c.name : undefined;

export const CompanyDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: canShow, isLoading: canShowLoading } = useCan({
    resource: 'companies',
    action: 'show',
  });
  const { data: canEditPermission, isLoading: canEditPermissionLoading } = useCan({
    resource: 'companies',
    action: 'edit',
  });
  // SF-4668: staff with a company list may view every company but only change the ones on it.
  // Every edit control on this page reads `canEdit`, so this one line locks them all.
  const {
    canEditCompany: companyOnList,
    isLoading: scopeLoading,
    isError: scopeCheckFailed,
    refetch: refetchScope,
  } = useCanEditCompany(id);
  const canEditLoading = canEditPermissionLoading || scopeLoading;
  const canEdit = { can: canEditPermission?.can === true && companyOnList };
  // SF-5055: a lookup that failed (no answer ever arrived) is not a refusal: say it could not check.
  const notOnListNote = scopeCheckFailed
    ? 'Could not check your company edit access. Try again.'
    : 'This company is not on your list of companies, so you can only view it.';
  // SF-3799 — only surface the Eva usage entry point to users who can read Eva
  // usage (same `eva:list` gate as the Eva overview page).
  const { data: canEva } = useCan({
    resource: 'eva',
    action: 'list',
  });
  // SF-3180 AC8 — SSO tab is super-admin / `admin:sso:read` only. Hide the
  // tab upfront so the list GET is never issued for an unauthorised user.
  const { data: canManageSso } = useCan({
    resource: 'sso',
    action: 'show',
  });

  // SF-4373 — the CSAT custom-domain add-on has its own tab, gated on
  // admin:companies:domain:read (not plain company rights), like SSO.
  const { data: permissions, isLoading: permsLoading } = usePermissions<string[]>();
  const canReadCsatDomain = hasPermission(permissions ?? [], CSAT_DOMAIN_READ_PERMISSION);
  const canWriteCsatDomain = hasPermission(permissions ?? [], CSAT_DOMAIN_WRITE_PERMISSION);
  const canWriteSso = hasPermission(permissions ?? [], SSO_WRITE_PERMISSION);

  const { queryResult } = useShow<Company>({
    resource: 'companies',
    meta: COMPANIES_META,
    queryOptions: { enabled: canShow?.can === true },
  });

  const company = queryResult?.data?.data;

  // SF-4576 — after the external AI access PATCH succeeds, write the returned
  // company into this page's detail-query cache (the same key useShow reads,
  // matched by prefix so the meta params do not matter). That cache is then
  // the only copy of the saved value: a failed refresh leaves the saved value
  // showing, and any later successful read (Refresh, refocus) replaces it.
  const queryClient = useQueryClient();
  const { keys, preferLegacyKeys } = useKeys();
  const { resources } = useResource();
  const companyDetailKey = (companyId: string | undefined) =>
    keys()
      .data(pickDataProvider('companies', undefined, resources))
      .resource('companies')
      .action('one')
      .id(companyId)
      .get(preferLegacyKeys);
  const writeCompanyToCache = (saved: Company, companyId: string | undefined = id) =>
    queryClient.setQueriesData<{ data: Company }>(companyDetailKey(companyId), (prev) =>
      prev ? { ...prev, data: { ...prev.data, ...saved } } : prev,
    );
  // SF-4656 — re-read the company a save was FOR. The page's own refetch
  // follows the URL, so after a jump from A to B it would re-read B: a
  // needless "could not refresh" on B, or a stale read of B landing over B's
  // own save (Bugbot). SF-5127: nor is its answer A's when staff move to B
  // while A's read is still running: it reports B's result. So always re-read
  // THAT company's cache entry, on screen or not, and take the answer from
  // that entry's own request. No entry to read means the company was not read.
  const refetchCompanyById = async (
    companyId: string | undefined,
  ): Promise<{ isError?: boolean }> => {
    if (!companyId) return { isError: true };
    const queryKey = companyDetailKey(companyId);
    const readable = queryClient
      .getQueryCache()
      .findAll({ queryKey })
      .some((query) => !query.isDisabled());
    if (!readable) return { isError: true };
    return queryClient
      .refetchQueries({ queryKey }, { throwOnError: true })
      .then(() => ({ isError: false }))
      .catch(() => ({ isError: true }));
  };

  // SF-5127: a successful read of a company from the server (the page's own, a saver's re-read,
  // a retry) clears that company's "could not be read again" mark. Cache writes (`manual`) do
  // not: they hold only what one save returned.
  const companyDetailKeyRef = useRef(companyDetailKey);
  companyDetailKeyRef.current = companyDetailKey;
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.type !== 'updated' || event.action.type !== 'success' || event.action.manual)
          return;
        const readId = (event.query.state.data as { data?: Company } | undefined)?.data?._id;
        if (readId === undefined || readId === null) return;
        const readKey = companyDetailKeyRef.current(String(readId));
        if (queryClient.getQueryCache().findAll({ queryKey: readKey }).includes(event.query))
          clearCompanyReadFailed(String(readId));
      }),
    [queryClient],
  );

  // SF-4813: after a delete-section action: put the deletion the server sent
  // into THAT company's cache first (so a failed re-read can't bring back the
  // old buttons), then re-read that company.
  const onDeletionChanged = (companyId: string, deletion?: CompanyDeletionRow | null) => {
    if (deletion !== undefined) {
      writeCompanyToCache(
        {
          deletion,
          deletion_status: deletion
            ? deletion.status === 'scheduled'
              ? 'scheduled'
              : 'deleting'
            : undefined,
        } as Company,
        companyId,
      );
    }
    void refetchCompanyById(companyId);
  };

  // SF-4656 — is SSO live for this company (a setup that is switched on, so
  // people can sign in with it)? A live setup counts as SSO on (tab shown,
  // switch locked on), so nobody hides the real off switch while sign-in still
  // works. A setup that is switched off does not: the flag can then go off and
  // the setup is kept. Only asked with admin:sso:read, like the SSO tab
  // itself. The tab reports its own saves/deletes back here (onConfigChange).
  const ssoSetupKey = (companyId: string | undefined) => ['companies', companyId, 'sso-setup'];
  const ssoSetup = useQuery({
    queryKey: ssoSetupKey(id),
    // 'none' | 'off' | 'live': only 'live' locks the switch, but the open SSO
    // tab follows every change, including an off setup created or deleted
    // elsewhere (Bugbot).
    queryFn: async () => ssoStateOf(await getCompanySsoConfig(authClient, id as string)),
    enabled: canShow?.can === true && canManageSso?.can === true && !!id,
    // Never show another company's answer while this one loads (Refine turns
    // keepPreviousData on for every query); the switch stays locked instead.
    keepPreviousData: false,
    // Re-read together with the company (below), not on its own schedule.
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Re-check the setup whenever THIS company is re-read after its first load
  // (the page's Refresh, refocus, after a save), so another admin creating or
  // deleting a setup shows up the same way their other company changes do
  // (Bugbot). The first read of a company is skipped: the setup query is
  // already asking for it. SF-5127: the help desk sending domain is re-read
  // then too, while the Help Desk tab shows it, so a domain that stopped
  // checking out (the daily check, another admin) updates with the sender.
  const companyReadAt =
    queryResult?.data?.data && id && String(queryResult.data.data._id) === id
      ? queryResult.dataUpdatedAt
      : 0;
  const lastCompanyRead = useRef<{ id?: string; readAt: number }>({ readAt: 0 });
  const ssoCheckAllowed = canShow?.can === true && canManageSso?.can === true && !!id;
  const refetchSsoSetup = ssoSetup.refetch;
  useEffect(() => {
    const last = lastCompanyRead.current;
    if (last.id !== id) {
      lastCompanyRead.current = { id, readAt: companyReadAt };
      return;
    }
    if (companyReadAt <= last.readAt) return;
    const firstRead = last.readAt === 0;
    lastCompanyRead.current = { id, readAt: companyReadAt };
    if (firstRead) return;
    if (ssoCheckAllowed) void refetchSsoSetup();
    if (id)
      void queryClient.refetchQueries({
        queryKey: sendingDomainKey(id),
        exact: true,
        type: 'active',
      });
  }, [id, companyReadAt, ssoCheckAllowed, refetchSsoSetup, queryClient]);

  // A setup saved (switched on or off) or deleted on the SSO tab: drop any read of the setup
  // still in flight first, or its older answer would land on top (review).
  const publishSsoSetup = async (companyId: string, state: SsoState) => {
    await queryClient.cancelQueries({ queryKey: ssoSetupKey(companyId) });
    queryClient.setQueryData(ssoSetupKey(companyId), state);
  };

  // One save at a time per company, for every control on the page (see
  // companySaveStore): this saver, the external AI switch and the SSO tab.
  const saver = useSaveCompanyField({
    companyId: id ?? '',
    companyName: currentCompanyName(queryResult?.data?.data, id),
    onSaved: writeCompanyToCache,
    refetchCompany: refetchCompanyById,
  });

  // Controlled so a tab that disappears (its feature switched off, or its SSO
  // setup deleted) drops back to Feature Flags instead of a blank pane. The
  // fallback is stored, not just shown: otherwise the old key would stay
  // selected and the tab would jump back into view the next time it appears
  // (Bugbot).
  const [activeTab, setActiveTab] = useState(() => tabFromUrl(searchParams) ?? 'general');
  // SF-5096: other screens link to `?tab=health`. The route element is reused across ids, so a
  // link followed while the page is open changes only the URL: select the tab it names then too.
  const urlTab = tabFromUrl(searchParams);
  useEffect(() => {
    if (urlTab) setActiveTab(urlTab);
  }, [urlTab, id]);
  // Picking another tab drops the param, so the URL never names a tab that is not open.
  const onTabChange = (key: string) => {
    setActiveTab(key);
    if (searchParams.has('tab')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('tab');
          return next;
        },
        { replace: true },
      );
    }
  };
  // SF-4762: company fields a linked platform flag switches on for every
  // company. Read once here so the Feature Flags switches and the setup tabs
  // below use the same effective value. Empty on load/error/no permission.
  const platformOnFields = usePlatformOnCompanyFields();

  if (canShowLoading || (canShow?.can === true && queryResult?.isLoading))
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;

  if (!canShow?.can) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle={canShow?.reason ?? 'You do not have permission to view this company.'}
      />
    );
  }

  // Only the company in the URL: Refine keeps the previous record while the
  // next one loads (keepPreviousData), and the route element is reused across
  // ids, so without this check a control could show, or act on, another
  // company's state (SF-4576).
  const currentCompany = company && id && String(company._id) === id ? company : undefined;
  // General Info still shows a record that is not (yet) the one in the URL,
  // but read-only: only the URL's company can be changed.
  const shownCompany = currentCompany ?? company;
  const canEditInline = canEdit?.can === true && currentCompany !== undefined;
  // SF-4813: the read of the URL's company failed and there is nothing of it to
  // show (first visit, or a move A to B where B failed while A is still held).
  // Not a load in progress, so the danger zone says so instead of a skeleton.
  const companyReadFailed = !currentCompany && queryResult.isError;
  const readError = queryResult.error as {
    statusCode?: number;
    response?: { status?: number };
  } | null;
  const companyNotFound = (readError?.statusCode ?? readError?.response?.status) === 404;

  // SF-4656 — a feature's setup tab shows only while its switch is on.
  // If the setup check fails, show the tab anyway: hiding the setup screen of
  // a company that may sign in with SSO is the worse mistake (the tab shows
  // its own load error).
  // A linked platform flag that is on counts as the switch being on (SF-4762).
  const ssoOn =
    currentCompany?.sso_enabled === true ||
    ssoSetup.data === 'live' ||
    ssoSetup.isError ||
    platformOnFields.has('sso_enabled');
  // While the CSAT switch itself is saving, the setup tab is hidden: the
  // server may already have changed the flag, and nothing (e.g. a test send)
  // may act on it until the refreshed company is back (SF-4373 rule).
  const csatSaving = saver.pending?.field === 'csat_custom_domain_enabled';
  const csatOn =
    (currentCompany?.csat_custom_domain_enabled === true ||
      platformOnFields.has('csat_custom_domain_enabled')) &&
    !csatSaving;
  // SF-5055: the Help Desk settings tab shows only while the company's Help Desk is on, and is
  // hidden while its switch (on Feature Flags) is saving, like CSAT. A value sf-api could not read
  // (null or missing) shows the tab: the switch is locked then, so hiding the tab would leave the
  // settings unreachable. While the URL's company is still loading (or its read failed) the tab
  // stays too, with its loading pane (or error and Retry), so an open tab never drops to a blank
  // pane before the company's own value is known.
  const helpDeskSaving = saver.pending?.field === 'help_desk_module_enabled';
  const helpDeskOn = currentCompany?.help_desk_module_enabled !== false && !helpDeskSaving;

  const generalInfoTab = (
    <Descriptions bordered column={1}>
      <Descriptions.Item label="Name">
        <Space size="middle" wrap>
          {company?.name || '—'}
          {/* SF-4770: only for the company in the URL, and only when sf-api sends the link. */}
          {currentCompany?.web_app_link && <WebAppLink href={currentCompany.web_app_link} />}
        </Space>
      </Descriptions.Item>
      <Descriptions.Item label="Internal Name">
        {company?.internal_name || '—'}
      </Descriptions.Item>
      {/* SF-4656 — changed right here, each saved on its own (not by the Edit form). */}
      <Descriptions.Item label="Active">
        {shownCompany ? (
          <ActiveSetting key={id} company={shownCompany} canEdit={canEditInline} saver={saver} />
        ) : (
          '—'
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Account Type">
        {shownCompany ? (
          <AccountTypeSetting key={id} company={shownCompany} canEdit={canEditInline} saver={saver} />
        ) : (
          '—'
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Sales Channel">
        {shownCompany ? (
          <SalesChannelSetting key={id} company={shownCompany} canEdit={canEditInline} saver={saver} />
        ) : (
          '—'
        )}
        {!canEditLoading && canEdit?.can !== true && (
          <div>
            <Typography.Text type="secondary" data-testid="company-settings-readonly-note">
              {canEditPermission?.can === true && !companyOnList
                ? notOnListNote
                : 'Changing Active, Account Type or Sales Channel needs company edit rights (admin:companies:write).'}
            </Typography.Text>
          </div>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Colour">
        {company?.color ? (
          <Space>
            <span
              aria-label={`Colour swatch ${company.color}`}
              style={{
                display: 'inline-block',
                width: 16,
                height: 16,
                backgroundColor: company.color,
                border: '1px solid #d9d9d9',
                borderRadius: 2,
                verticalAlign: 'middle',
              }}
            />
            <Typography.Text code>{company.color}</Typography.Text>
          </Space>
        ) : (
          '—'
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Privacy Link">
        {company?.privacy_link ? (
          <a href={company.privacy_link} target="_blank" rel="noopener noreferrer">
            {company.privacy_link} <LinkOutlined />
          </a>
        ) : (
          '—'
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Created At">
        {company?.createdAt ? new Date(company.createdAt).toLocaleString() : '—'}
      </Descriptions.Item>
      {/* SF-4799: only for the company in the URL (the previous one stays cached while the next loads). */}
      <Descriptions.Item label="Created By">{createdByLabel(currentCompany)}</Descriptions.Item>
    </Descriptions>
  );

  // Tabs are defined as items (AntD v5 API).
  const tabItems = [
    {
      key: 'general',
      label: 'General Info',
      children: (
        <>
          {generalInfoTab}
          {/* SF-4665 — fake data batches; renders null without admin:demos:create.
              Keyed by company so nothing opened for one company acts on the next. */}
          {id ? (
            <div style={{ marginTop: 24 }}>
              <DemoDataSection key={id} companyId={id} />
            </div>
          ) : null}
          {/* SF-4324 — permission-gated hard-delete danger zone. A company-level
              action, so it belongs to General Info only — not trailing the SSO
              or CSAT Custom Domain tabs. Renders null without
              admin:companies:delete. */}
          {id ? (
            <div style={{ marginTop: 24 }}>
              {/* SF-4813: state comes from this company's own read only (never the
                  previous company's record), and each action re-reads the company
                  it started on. Keyed by id so dialogs and errors reset per company. */}
              <DeleteCompanySection
                key={id}
                companyId={id}
                companyName={currentCompany?.name ?? company?.name}
                deletionStatus={currentCompany?.deletion_status}
                deletion={currentCompany?.deletion}
                graceDays={currentCompany?.deletion_grace_days}
                isActive={currentCompany?.is_active}
                loading={!currentCompany && !companyReadFailed}
                loadError={companyReadFailed ? (companyNotFound ? 'not-found' : 'failed') : null}
                onRetryLoad={() => void queryResult.refetch()}
                retryingLoad={queryResult.isFetching}
                onChanged={onDeletionChanged}
              />
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: 'flags',
      label: 'Feature Flags',
      children:
        currentCompany && id ? (
          // key: a confirmation opened for one company must never confirm
          // against the next one (the route element is reused across ids).
          <FeatureFlagsTab
            key={id}
            company={currentCompany}
            companyId={id}
            canEditCompany={canEditPermission?.can === true}
            companyOnList={companyOnList}
            scopeCheckFailed={scopeCheckFailed}
            canReadSso={canManageSso?.can === true}
            canWriteSso={canWriteSso}
            canReadCsatDomain={canReadCsatDomain}
            canWriteCsatDomain={canWriteCsatDomain}
            platformOnFields={platformOnFields}
            permissionsLoading={permsLoading || canEditLoading}
            ssoSetup={{
              live: ssoSetup.data === undefined ? undefined : ssoSetup.data === 'live',
              // Also while re-checking: act only on a settled answer.
              isLoading: ssoSetup.fetchStatus === 'fetching',
              isError: ssoSetup.isError,
            }}
            saver={saver}
            // Bound to this render's company: a save started here finishes
            // against it even if the URL has moved on.
            onSaved={(saved) => writeCompanyToCache(saved, id)}
            refetchCompany={() => refetchCompanyById(id)}
          />
        ) : null,
    },
    ...(canManageSso?.can && ssoOn
      ? [
          {
            key: 'sso',
            label: 'SSO Configuration',
            // SF-3180 Task 1: load + show current SSO config for this company.
            // Guarded by `id` so we don't issue a fetch for a half-mounted page.
            children: id ? (
              <SsoConfigTab
                key={id}
                companyId={id}
                companyName={currentCompany?.name}
                pageSsoState={ssoSetup.fetchStatus === 'idle' ? ssoSetup.data : undefined}
                onConfigChange={(forCompany, state) => void publishSsoSetup(forCompany, state)}
              />
            ) : null,
          },
        ]
      : []),
    // SF-5055: every help desk setting (the web app's plan, inbox and ratings, and Eva's replies),
    // only while Help Desk is on (its switch is on Feature Flags). Read needs company view rights;
    // changing anything needs admin:companies:write and the company on the staff member's list.
    ...(helpDeskOn
      ? [
          {
            key: 'help-desk',
            label: 'Help Desk',
            children:
              currentCompany && id ? (
                // key: a confirmation opened for one company must never act on the next one.
                <HelpDeskTab
                  key={id}
                  company={currentCompany}
                  canEditCompany={canEditPermission?.can === true}
                  companyOnList={companyOnList}
                  scopeCheckFailed={scopeCheckFailed}
                  onRetryScope={() => void refetchScope()}
                  onRetrySender={() => void refetchCompanyById(id)}
                  permissionsLoading={permsLoading || canEditLoading}
                  saver={saver}
                />
              ) : companyReadFailed ? (
                // A failed read is not a load in progress: say so, with a retry (never a blank tab).
                companyNotFound ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="Company not found"
                    data-testid="help-desk-load-error"
                  />
                ) : (
                  <Alert
                    type="error"
                    showIcon
                    message="Could not load this company."
                    data-testid="help-desk-load-error"
                    action={
                      <Button
                        size="small"
                        loading={queryResult.isFetching}
                        onClick={() => void queryResult.refetch()}
                      >
                        Retry
                      </Button>
                    }
                  />
                )
              ) : (
                <Spin
                  data-testid="help-desk-loading"
                  style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}
                />
              ),
          },
        ]
      : []),
    // SF-4779: company security settings, next to SSO Configuration. Read
    // needs company view rights; changing anything needs admin:companies:write.
    {
      key: 'security',
      label: 'Security',
      children:
        currentCompany && id ? (
          <SecurityTab
            key={id}
            company={currentCompany}
            companyId={id}
            canWrite={canEdit.can}
            permissionsLoading={canEditLoading}
            readOnlyReason={
              canEditPermission?.can === true && !companyOnList
                ? notOnListNote
                : 'Changing this needs company edit rights (admin:companies:write).'
            }
            saver={saver}
          />
        ) : null,
    },
    // SF-5096: the company's platform usage. Read only, and needs only company view rights (the
    // usage routes ask for admin:companies:read, the same as this page). Keyed by id so the
    // chosen month and any answer in hand never carry over to the next company.
    {
      key: 'health',
      label: 'Health',
      children: id ? <HealthTab key={id} companyId={id} /> : null,
    },
    ...(canReadCsatDomain && csatOn
      ? [
          {
            key: 'csat-domain',
            label: 'CSAT Custom Domain',
            // SF-4373 setup screen; its on/off switch is on Feature Flags (SF-4656).
            // The URL's company only, keyed by it: the radio saves through the
            // page's per-company saver, like every other control here.
            children: currentCompany ? (
              <CsatDomainTab
                key={id}
                company={currentCompany}
                canEditCompany={canEditPermission?.can === true}
                companyOnList={companyOnList}
                saver={saver}
              />
            ) : null,
          },
        ]
      : []),
  ];

  const shownTab = tabItems.some((t) => t.key === activeTab) ? activeTab : 'flags';
  // Store the fallback only once this company has fully loaded (its record
  // and, with SSO rights, its setup check). While the next company loads the
  // setup tabs are briefly missing; that gap must not unselect a tab the new
  // company also has (Bugbot).
  const tabsSettled =
    currentCompany !== undefined && (canManageSso?.can !== true || ssoSetup.fetchStatus === 'idle');
  if (shownTab !== activeTab && tabsSettled) setActiveTab(shownTab);

  return (
    <Show
      title={
        <Space>
          {company?.name || 'Company Detail'}
          {/* SF-5100: only for the company in the URL, not the previous one while it loads. */}
          <ServeFirstLiveTag accountType={currentCompany?.account_type} />
        </Space>
      }
      // Suppress Refine's built-in EditButton in defaultButtons so we don't
      // render two Edit actions — we manage the gated custom one ourselves.
      canEdit={false}
      headerButtons={({ defaultButtons }) => (
        <Space>
          {/* Keyed by company: the route is reused across ids, so moving on closes the drawer
              and the next company's history is read under its own key. Single sign-on setup
              changes join it only for viewers who see the SSO Configuration tab. */}
          {id ? (
            <ChangeLogLink
              key={id}
              subject={{ kind: 'company', companyId: id, includeSso: canManageSso?.can === true }}
            />
          ) : null}
          {defaultButtons}
          {canEva?.can ? (
            <Button
              icon={<ExportOutlined />}
              onClick={() => navigate(`/eva/companies/${id}`)}
            >
              Eva Usage
            </Button>
          ) : null}
          {canEdit?.can ? (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/companies/${id}/edit`)}
            >
              Edit
            </Button>
          ) : null}
        </Space>
      )}
    >
      <Tabs items={tabItems} activeKey={shownTab} onChange={onTabChange} />
    </Show>
  );
};
